import { useEffect, useRef, useState } from 'react';

export default function Contact() {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showLightning, setShowLightning] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 3000);
      return;
    }

    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service: formData.service,
        message: formData.message,
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d4incgb3rief4cfea64g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody,
      });

      if (response.ok) {
        setShowLightning(true);
        setTimeout(() => {
          setSubmitStatus('success');
          setShowLightning(false);
        }, 800);
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 3000);
    }
  };

  const contactInfo = [
    {
      icon: 'ri-map-pin-line',
      title: 'Visit Us',
      content: 'Avda Virgen del Rocío 1',
      subcontent: '29670, Marbella (Málaga), Spain',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: 'ri-phone-line',
      title: 'Call Us',
      content: '+34 664 428 424',
      subcontent: 'Mon-Fri: 8:00 AM - 6:00 PM',
      color: 'from-purple-500 to-pink-500',
    },
    {
      icon: 'ri-mail-line',
      title: 'Email Us',
      content: 'conproevolucionsl@gmail.com',
      subcontent: 'We reply within 24 hours',
      color: 'from-orange-500 to-red-500',
    },
  ];

  const whyChooseUs = [
    {
      icon: 'ri-shield-check-line',
      title: 'Licensed & Insured',
      description: 'Fully certified with comprehensive insurance coverage for your peace of mind',
    },
    {
      icon: 'ri-time-line',
      title: 'On-Time Delivery',
      description: 'We respect your schedule and deliver projects within agreed timelines',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: '24/7 Support',
      description: 'Always available to answer your questions and address your concerns',
    },
  ];

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="relative py-32 bg-gray-900 overflow-hidden"
    >
      {/* Lightning Flash Effect */}
      {showLightning && (
        <div className="fixed inset-0 z-50 pointer-events-none">
          <div className="absolute inset-0 bg-orange-400 animate-lightning-flash"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-gray-900 rounded-3xl p-8 shadow-2xl border-4 border-orange-500 animate-lightning-popup">
              <div className="flex items-center gap-4">
                <i className="ri-flashlight-fill text-6xl text-orange-500 animate-pulse"></i>
                <div>
                  <h3 className="text-3xl font-bold text-white mb-2">⚡ Message Sent!</h3>
                  <p className="text-gray-300 text-lg">We'll get back to you soon</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-orange-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-amber-500 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div
            className={`inline-block mb-4 px-6 py-2 bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-500/20 rounded-full transform transition-all duration-1000 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <span className="text-orange-500 font-semibold">Get in Touch</span>
          </div>
          
          <h2
            className={`text-5xl md:text-6xl font-bold mb-6 transform transition-all duration-1000 delay-100 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <span className="bg-gradient-to-r from-white via-orange-100 to-amber-100 bg-clip-text text-transparent">
              Let's Build Together
            </span>
          </h2>
          
          <p
            className={`text-xl text-gray-400 max-w-3xl mx-auto transform transition-all duration-1000 delay-200 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            Ready to start your next construction project? Contact us today for a consultation
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {contactInfo.map((info, index) => (
            <div
              key={index}
              className={`group relative bg-gray-800 rounded-3xl p-8 shadow-lg shadow-black/50 hover:shadow-2xl hover:shadow-orange-500/20 transform transition-all duration-500 hover:-translate-y-2 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
              style={{ transitionDelay: `${300 + index * 100}ms` }}
            >
              <div className={`w-16 h-16 mb-6 bg-gradient-to-br ${info.color} rounded-2xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg`}>
                <i className={`${info.icon} text-3xl text-white`}></i>
              </div>
              
              <h3 className="text-xl font-bold text-white mb-3">{info.title}</h3>
              <p className="text-orange-400 font-semibold mb-1">{info.content}</p>
              <p className="text-gray-400 text-sm">{info.subcontent}</p>

              <div className={`absolute top-4 right-4 w-24 h-24 bg-gradient-to-br ${info.color} rounded-full blur-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-500`}></div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div
            id="contact-form"
            className={`transform transition-all duration-1000 delay-400 ${
              isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
            }`}
          >
            <div className="bg-gray-800 rounded-3xl p-8 shadow-xl shadow-black/50">
              <h3 className="text-3xl font-bold text-white mb-6">Send us a Message</h3>
              
              <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form>
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-300 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 text-sm text-white"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-300 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 text-sm text-white"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-300 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 text-sm text-white"
                    placeholder="+34 XXX XXX XXX"
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-semibold text-gray-300 mb-2">
                    Service Interested In
                  </label>
                  <div className="relative">
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                      className="w-full px-4 py-3 pr-8 bg-gray-900 border border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 appearance-none cursor-pointer text-sm text-white"
                    >
                      <option value="">Select a service</option>
                      <option value="construction">Specialized Construction</option>
                      <option value="equipment">Equipment Rental</option>
                      <option value="renovation">Renovation Services</option>
                      <option value="consulting">Consulting</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-300 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setFormData({ ...formData, message: e.target.value });
                      }
                    }}
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 resize-none text-sm text-white"
                    placeholder="Tell us about your project..."
                  ></textarea>
                  <div className="text-right text-xs text-gray-500 mt-1">
                    {formData.message.length}/500 characters
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-4 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-full font-semibold text-lg hover:shadow-xl hover:shadow-orange-500/50 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-900/50 border border-red-500 rounded-xl text-red-300 text-center">
                    Please fill in all required fields.
                  </div>
                )}
              </form>
            </div>
          </div>

          <div
            className={`transform transition-all duration-1000 delay-500 ${
              isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
            }`}
          >
            <div className="bg-gray-800 rounded-3xl p-8 shadow-xl shadow-black/50 h-full">
              <h3 className="text-3xl font-bold text-white mb-8">Why Choose CONPRO?</h3>
              
              <div className="space-y-6">
                {whyChooseUs.map((item, index) => (
                  <div
                    key={index}
                    className="group bg-gray-900 rounded-2xl p-6 hover:bg-gray-700 transition-all duration-300 transform hover:-translate-y-1"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center flex-shrink-0 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                        <i className={`${item.icon} text-2xl text-white`}></i>
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-white mb-2 group-hover:text-orange-400 transition-colors duration-300">
                          {item.title}
                        </h4>
                        <p className="text-gray-400 leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-6 bg-gradient-to-br from-orange-600/20 to-amber-600/20 border border-orange-500/30 rounded-2xl">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                    <i className="ri-star-fill text-2xl text-white"></i>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-white">100%</div>
                    <div className="text-orange-400 font-semibold">Client Satisfaction</div>
                  </div>
                </div>
                <p className="text-gray-300 text-sm">
                  Over 500 successful projects completed across Costa del Sol with outstanding results
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
